import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { connect } from 'react-redux';

import { ContainerHeader, ErrorAlert } from '@src/components';
import { Link } from '@src/components/link';
import { Button, Card, modalConfirm } from '@src/controls';
import { DOMAIN, ERRORS, LABELS, MESSAGES } from '@src/dictionaries';
import { RootState } from '@src/modules/';
import { authSelectors } from '@src/modules/auth';
import { eventTrackingActions } from '@src/modules/event-tracking';
import { tagsActions } from '@src/modules/tags';
import { userprofileActions } from '@src/modules/userprofile';
import TemplateList from './components/template-list';
import { excludedSiteIds, NewExperienceAlert } from '@src/components/new-experience-alert';
import { getTransportUrl } from '@src/services/transport-service';

// Component API
type OwnProps = {
  siteId: string;
};

const mapStateToProps = (state: RootState) => ({
  error: state.userprofile.error,
  pageSize: state.app.pageSize,
  isFetching: state.userprofile.isFetching,
  items: state.userprofile.templates,
  userId: authSelectors.getAuthUserId(state),
});

const dispatchProps = {
  getTemplates: userprofileActions.getTemplateListRequest,
  deleteTemplates: userprofileActions.deleteTemplatesRequest,
  templateListPageView: eventTrackingActions.templateListPageView,
  getSiteTags: tagsActions.listSiteTagsRequest,
};

type Props = ReturnType<typeof mapStateToProps> & typeof dispatchProps & OwnProps;

@observer
class TemplateListContainer extends React.Component<Props> {
  @observable currentPage = 1;

  componentDidMount() {
    const { siteId, getTemplates, templateListPageView, getSiteTags, userId } = this.props;

    if (this.shouldRedirectToTransport()) {
      window.location.href = this.getTransportUrl();
      return;
    }

    templateListPageView();
    getSiteTags({
      siteId,
      userId,
      onComplete: tags =>
        getTemplates({
          siteId,
          tags: tags.map(tag => tag.id),
        }),
    });
  }

  handlePaginationChange = (nextPage: number) => {
    this.currentPage = nextPage;
  };

  handleDeleteClick = (templateIds: string[], onDeleteCb: () => void) => {
    const { siteId } = this.props;

    modalConfirm({
      title: MESSAGES.DELETE_TEMPLATES_HEADER_LIST_VIEW(templateIds.length),
      content: MESSAGES.DELETE_TEMPLATES_CONTENT_LIST_VIEW(templateIds.length),
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => {
        this.props.deleteTemplates({ siteId, templateIds });
        onDeleteCb();
      },
    });
  };

  shouldRedirectToTransport = () => {
    return !excludedSiteIds.includes(this.props.siteId ?? '');
  };

  getTransportUrl = () => {
    return getTransportUrl(this.props.siteId, '/settings/shipment-templates');
  };

  render() {
    if (this.shouldRedirectToTransport()) {
      return null;
    }

    const { items, isFetching, error } = this.props;

    return (
      <div>
        <NewExperienceAlert link={this.getTransportUrl()} />

        <ErrorAlert
          error={error}
          visible={!!error}
          showIcon={true}
          banner={true}
          closeText={ERRORS.CLOSE_ALERT}
        />
        <Card bordered={false}>
          <ContainerHeader
            title={DOMAIN.TEMPLATES}
            actions={
              <Link route={{ name: 'TEMPLATE_CREATE' }}>
                <Button type="primary"> {LABELS.CREATE}</Button>
              </Link>
            }
          />
          <TemplateList
            addressBookError={error}
            isLoading={isFetching}
            total={items.length}
            currentPage={this.currentPage}
            onPaginationChange={this.handlePaginationChange}
            items={items}
            onDelete={this.handleDeleteClick}
          />
        </Card>
      </div>
    );
  }
}

export default connect(mapStateToProps, dispatchProps)(TemplateListContainer);
